/**
 * Contract types for the PRR-309 Pay Run BSB verification endpoints.
 *
 * These mirror the backend DTOs exactly. The vocabularies below are lowercase strings on the wire, not enums, so they
 * are modelled as unions with a `string` escape so an unrecognised future value cannot crash the surface.
 */

/** Overall validation status for the Pay Run's current BSB evidence. */
export type BsbValidationStatus = 'passed' | 'blocked' | 'override_required';

/** Per-finding severity. Blockers are never overridable. */
export type BsbFindingSeverity = 'blocker' | 'warning';

/**
 * Directory lifecycle state for the checked BSB. `directory` is the synthetic value the backend uses for run-level
 * findings, which carry no worker or BSB and must never be rendered as a worker row.
 */
export type BsbDirectoryStatus = 'active' | 'merged' | 'withdrawn' | 'unknown' | 'directory' | (string & {});

/** Rule codes emitted by the PRR-309 validation policy. */
export type BsbRuleCode =
  | 'WithdrawnBsb'
  | 'SupersededBsb'
  | 'NonElectronicBsb'
  | 'StaleBsbDirectory'
  | 'UnknownBsb'
  | 'BsbDirectoryUnavailable'
  | (string & {});

/** Rule codes the backend raises at run level rather than against a worker. */
export const RUN_LEVEL_BSB_RULE_CODES = ['StaleBsbDirectory', 'BsbDirectoryUnavailable'] as const;

/**
 * Sentinel ingestion ids the backend returns when there was no directory read to describe. In both cases
 * `directoryCompletedAt` is the Unix epoch and must not be shown as a real "directory as at" date.
 */
export const BSB_DIRECTORY_SENTINEL_IDS = ['not-required', 'unavailable'] as const;

export interface BsbValidationSummary {
  validationId: string;
  status: BsbValidationStatus | (string & {});
  destinationSetDigest: string;
  findingCount: number;
  blockingCount: number;
  unresolvedOverrideCount: number;
  validatedAt: string;
}

export interface BsbValidationFinding {
  findingId: string;
  validationIdentity: string;
  personId: string;
  employmentRecordId: string;
  destinationSnapshotId: string;
  bsb: string;
  ruleCode: BsbRuleCode;
  severity: BsbFindingSeverity | (string & {});
  overrideAllowed: boolean;
  overridden: boolean;
  directoryStatus: BsbDirectoryStatus;
  isElectronic: boolean | null;
  streamCode: string | null;
  supersededByBsb: string | null;
  /** Projected from the pinned immutable population snapshot. Null for run-level findings and unmatched workers. */
  workerDisplayName: string | null;
}

export interface BsbValidationDetails {
  summary: BsbValidationSummary;
  directoryIngestionId: string;
  directoryCompletedAt: string;
  refreshCadenceHours: number;
  directoryWasStale: boolean;
  findings: BsbValidationFinding[];
}

/** Discriminator for the GET .../bsb-validation response. Always served with HTTP 200. */
export type BsbValidationState = 'not_evaluated' | 'evaluated' | (string & {});

/**
 * Wire response for the read endpoint. `not_evaluated` (with a null `details`) is a legitimate state meaning the Pay
 * Run has no current BSB evidence yet (evidence is only produced once the compliance engine processes a Ready to Pay
 * request) — it is never surfaced as an HTTP error.
 */
export interface BsbValidationResponse {
  state: BsbValidationState;
  details: BsbValidationDetails | null;
}
